/**
 * @fileoverview Polls the backend alert system (admin-only) and surfaces the
 * "Cal eProcure scraper needs a manual sign-in" condition so it can NEVER fail
 * silently. Backed by the existing /api/alerts (firing alerts from the notifier
 * `alert_state` table). The scraper's session-prober raises
 * `cal_eprocure_login_required`; the detail-sweep raises `rfp_sweep_stalled`
 * with `context.needs_login` — we surface either. The detail-sweep also raises
 * `rfp_popup_blocked` when >20% of a batch fails because Cal eProcure refused
 * the detail popup (attachments can't be opened) — surfaced as a softer warning.
 * @module hooks/useScraperLoginAlert
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/apiClient';
import { useRole } from './useRole';

/**
 * @returns {{ loginAlert: object|null, popupAlert: object|null, isAdmin: boolean }}
 *   loginAlert / popupAlert: the firing alert object (or null). Shape from the
 *   notifier: { fingerprint, kind, severity, current_message, current_context,
 *   last_fired_at }.
 */
export function useScraperLoginAlert() {
  const { isAdmin } = useRole();

  // /api/alerts is admin-gated on the backend; only poll for admins.
  const { data } = useQuery({
    queryKey: ['active-alerts'],
    queryFn: () => apiClient.get('/api/alerts'),
    enabled: !!isAdmin,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
  });

  const alerts = data?.alerts || [];
  const loginAlert = alerts.find((a) => {
    if (a.kind === 'cal_eprocure_login_required') return true;
    const ctx = a.current_context || a.context || {};
    return a.kind === 'rfp_sweep_stalled' && !!ctx.needs_login;
  }) || null;
  const popupAlert = alerts.find((a) => a.kind === 'rfp_popup_blocked') || null;

  return { loginAlert, popupAlert, isAdmin: !!isAdmin };
}

export default useScraperLoginAlert;
